import { createFileRoute } from '@tanstack/react-router';

import { Alert, Badge, Card, PageHeader, PageLayout, Skeleton } from '@keboola/design';

import { useAuth } from '../../auth/context';
import { MfaEnrolment } from '../../features/vendors/components/MfaEnrolment';
import { describeMfa } from '../../features/vendors/mfaStatus';
import { useProfile } from '../../features/vendors/queries';

export const Route = createFileRoute('/admin/profile')({
  component: AdminProfilePage,
  head: () => ({ meta: [{ title: 'Profile | Keboola Developer Portal admin' }] }),
});

/**
 * The admin's own account.
 *
 * Deliberately thinner than the vendor profile: no vendor tiles, because an admin's relationship to
 * vendors is the `/admin/vendors` screen, not a list of their own memberships. The MFA half is the same
 * component the vendor profile and the dashboard gate use.
 */
function AdminProfilePage() {
  const { identity } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const mfa = describeMfa(profile?.mfa);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Profile"
          description="Your account and multi-factor authentication."
        />
      </PageLayout.Header>

      <Card className="tw:max-w-xl">
        <Card.Header>
          <Card.Title as="h2">Details</Card.Title>
        </Card.Header>
        <Card.Content className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:flex tw:flex-col">
            <span className="tw:flex tw:items-center tw:gap-2 tw:font-medium">
              {identity?.name ?? '—'}
              {identity?.isAdmin === true && <Badge color="purple-inverse">Admin</Badge>}
            </span>
            {identity?.email !== null && identity?.email !== undefined && (
              <a
                href={`mailto:${identity.email}`}
                className="tw:text-sm tw:text-primary tw:underline"
              >
                {identity.email}
              </a>
            )}
          </div>

          <hr className="tw:border-border" />

          <div className="tw:flex tw:flex-col tw:items-start tw:gap-3">
            <p className="tw:m-0 tw:text-sm">
              Multi-factor authentication:{' '}
              {isLoading ? (
                <Skeleton className="tw:inline-block tw:h-4 tw:w-20 tw:align-middle" />
              ) : (
                <strong>{mfa.label}</strong>
              )}
            </p>

            {mfa.isDeprecated && (
              <Alert variant="warning">
                SMS-based verification is being retired. Set up an authenticator app to keep access
                to your account.
              </Alert>
            )}

            <MfaEnrolment
              label={mfa.actionLabel}
              variant={mfa.isDeprecated ? 'primary' : 'secondary'}
            />
          </div>
        </Card.Content>
      </Card>
    </PageLayout>
  );
}
